import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import Basic from "@/components/seatmaps/Basic";
import Editor from "@/components/seatmaps/Editor";
import ToastContainer from "@/components/ui/ToastContainer";
import { io } from "socket.io-client";
import { useCurrency } from "@/contexts/CurrencyContext";
import BoxBlock from "@/components/ui/BoxBlock";
import Layout from "@/components/ui/Layout";
import dynamic from "next/dynamic";
import DOMPurify from "isomorphic-dompurify";
import { withI18nStaticProps } from "@/lib/withI18nStaticProps";
import Link from "next/link";
import Image from "next/image";
import AlertBlock from "@/components/ui/AlertBlock";
import SeatmapInteractionGuard from "@/components/seatmaps/SeatmapInteractionGuard";
import SeatModal from "@/components/seatmaps/SeatModal";

const Leafleet = dynamic(() => import("@/components/Leafleet"), {
    ssr: false,
});

function renderDescriptionHtml(html) {
    if (!html) return null;

    const safe = DOMPurify.sanitize(html, {
        USE_PROFILES: { html: true }, // jó default
    });

    return (
        <div
            className="prose dark:prose-invert max-w-none"
            dangerouslySetInnerHTML={{ __html: safe }}
        />
    );
}

import {
    Loader2,
    Info,
    Sofa,
    Navigation,
    Wine,
    X,
    Calendar,
    LetterText,
    Image as ImageIcon,
    MapPin,
    AlertTriangle, RefreshCw, SquareDashedMousePointer
} from "lucide-react";
import { useTranslation } from "@/contexts/TranslationContext";

let socket = null;

import Head from "next/head";

function pickFinalPriceNumber(v) {
    const n =
        Number(v?.price?.base_final) ||
        Number(v?.price?.unit_price_final) ||
        Number(v?.base_price) ||
        0;
    return n > 0 ? n : null;
}

function stripHtml(input) {
    return (input ?? "")
        .toString()
        .replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

function getEventStartDates(product, isAddonVariant) {
    const dates =
        product?.product_variants
            ?.filter(v => !isAddonVariant(v))
            ?.map(v =>
                v?.product_variant_attributes?.find(a => a?.attribute?.key === "event_start_time")
                    ?.custom_value_date
            )
            ?.filter(Boolean) || [];

    // uniq + valid
    const uniq = Array.from(new Set(dates.map(d => new Date(d).toISOString())));
    return uniq.filter(d => !Number.isNaN(new Date(d).getTime()));
}

function isGiftVariant(variant) {
    return variant?.product_variant_attributes?.some(
        (attr) => attr?.attribute?.key === "is_gift" && attr?.custom_value_text === "true"
    );
}

function addHoursIso(iso, hours) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    d.setHours(d.getHours() + hours);
    return d.toISOString();
}

function buildJsonLd({
    product,
    isAddonVariant,
    absUrl,
    absImage,
    currencyCode,
    publicAddress,
    seatmapName
}) {
    const sellable = (product?.product_variants || []).filter(v => !isAddonVariant(v));
    const prices = sellable.map(pickFinalPriceNumber).filter(Boolean);
    if (sellable.length === 0 || prices.length === 0) return null;

    const eventDates = getEventStartDates(product, isAddonVariant);
    const isEvent = eventDates.length > 0;

    if (isEvent) {
        const startDate = eventDates.slice().sort()[0];
        const endDate = addHoursIso(startDate, 2);

        const vendorOrg = buildVendorOrg(product?.vendor);

        return {
            "@context": "https://schema.org",
            "@type": "Event",
            "name": product?.name || "",
            "startDate": startDate,
            "endDate": endDate || undefined,
            "eventStatus": "https://schema.org/EventScheduled",
            "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
            "url": absUrl,
            "image": absImage ? [absImage] : undefined,
            "description": stripHtml(product?.description),

            "organizer": vendorOrg || undefined,

            "location": {
                "@type": "Place",
                "name": seatmapName || "Helyszín",
                ...(publicAddress
                    ? { "address": { "@type": "PostalAddress", "streetAddress": publicAddress } }
                    : {})
            },
            "offers": sellable
                .map(v => {
                    const price = pickFinalPriceNumber(v);
                    if (!price) return null;
                    return {
                        "@type": "Offer",
                        "url": absUrl,
                        "price": price,
                        "priceCurrency": currencyCode || "HUF",
                        "validFrom": product?.createdAt || undefined,
                    };
                })
                .filter(Boolean)
        };
    }

    const lowPrice = Math.min(...prices);
    const highPrice = Math.max(...prices);

    return {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": product?.name || "",
        "url": absUrl,
        "image": absImage ? [absImage] : undefined,
        "description": stripHtml(product?.description),
        "offers": {
            "@type": "AggregateOffer",
            "priceCurrency": currencyCode || "HUF",
            "lowPrice": lowPrice,
            "highPrice": highPrice,
            "offerCount": sellable.length
        }
    };
}

function buildVendorOrg(vendor) {
    const name = (vendor?.name || "").trim();
    if (!name) return null;

    return {
        "@type": "Organization",
        "name": name,
    };
}

export default function ProductPage({ initialProduct, initialPriceType, initialSeatmapData, initialSeats }) {
    const router = useRouter();
    const { slug } = router.query;

    const { t } = useTranslation();

    const [product, setProduct] = useState(initialProduct);

    const [showFullDescription, setShowFullDescription] = useState(false);

    const [expandedAddonDescriptions, setExpandedAddonDescriptions] = useState({});
    const toggleAddonDesc = (variantId) => {
        setExpandedAddonDescriptions((prev) => ({
            ...prev,
            [variantId]: !prev[variantId],
        }));
    };

    const [expandedNormalDescriptions, setExpandedNormalDescriptions] = useState({});
    const toggleNormalDesc = (variantId) => {
        setExpandedNormalDescriptions((prev) => ({
            ...prev,
            [variantId]: !prev[variantId],
        }));
    };

    const [seats, setSeats] = useState(initialSeats || []);
    const [seatsLoading, setSeatsLoading] = useState(false);

    const [selectedSeats, setSelectedSeats] = useState([]);

    const [toasts, setToasts] = useState([]);

    const [websocketFailed, setWebsocketFailed] = useState(false);

    const [pendingSeatId, setPendingSeatId] = useState(null); //null
    const [showSeatModal, setShowSeatModal] = useState(false); //false

    const [showAddonImageModal, setShowAddonImageModal] = useState(false);
    const [activeAddonImage, setActiveAddonImage] = useState(null); // url string

    useEffect(() => {
        if (!showAddonImageModal) return;

        const onKeyDown = (e) => {
            if (e.key === "Escape") {
                e.preventDefault();
                setShowAddonImageModal(false);
                setActiveAddonImage(null);
            }
        };

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [showAddonImageModal]);

    const SOCKET_URL = process.env.NEXT_PUBLIC_SOCKET_URL;

    const [reservedSeatsLoading, setReservedSeatsLoading] = useState(false);
    const [reservedSeatsLoadedOnce, setReservedSeatsLoadedOnce] = useState(false);

    const reservedReqIdRef = useRef(0);

    const AddToCartButton = ({ disabled, loading = false, onClick, labelKey }) => {
        const isDisabled = disabled || loading;

        return (
            <button
                disabled={isDisabled}
                onClick={(e) => {
                    if (isDisabled) return;
                    onClick?.(e);
                }}
                className={`px-3 py-1 rounded-md transition inline-flex items-center justify-center gap-2
        ${isDisabled
                        ? "bg-gray-300 text-gray-600 dark:bg-zinc-700 dark:text-zinc-300 cursor-not-allowed opacity-80"
                        : "bg-black text-white dark:bg-white dark:text-black hover:opacity-80 cursor-pointer"
                    }`}
            >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {t(labelKey)}
            </button>
        );
    };

    const [simpleAddLoading, setSimpleAddLoading] = useState({});
    const [seatConfirmLoading, setSeatConfirmLoading] = useState(false);

    const handleSimpleAddToCartWithLoading = async (variantId) => {
        if (simpleAddLoading[variantId]) return;

        setSimpleAddLoading((p) => ({ ...p, [variantId]: true }));
        try {
            await handleSimpleAddToCart(variantId);
        } finally {
            setSimpleAddLoading((p) => ({ ...p, [variantId]: false }));
        }
    };

    const { currency, formatCurrency } = useCurrency();
    function getPriceForCurrency(variant) {
        if (!variant?.price?.base_final) return null;
        if (!currency) return null;

        return {
            amount: formatCurrency(variant.price.base_final),
            symbol: currency.symbol,
            code: currency.code
        };
    }

    function renderPrice(price) {
        if (!price) return t("product.no.price");
        if (Number.isNaN(price.amount)) return t("product.no.price");

        return `${price.amount} ${price.symbol || ""}`;
    }

    function getCartOpAction(json, { type, seatId, variantId } = {}) {
        const ops = Array.isArray(json?.operations) ? json.operations : [];

        return (
            ops.find(op =>
                (!type || op.type === type) &&
                (seatId == null || String(op.seatId) === String(seatId)) &&
                // ha később van variantId a műveletben, itt tudod szűrni:
                (variantId == null || String(op.product_variant) === String(variantId))
            )?.action || null
        );
    }


    function trackGaCart(eventName, variant, qty = 1) {
        if (typeof window === "undefined") return;
        if (!variant) return;

        const unit = Number(
            variant?.price?.unit_price_final ??
            variant?.price?.base_final ??
            variant?.base_price ??
            0
        );

        const payload = {
            currency: currency?.code || "HUF",
            value: unit * qty,
            items: [buildGaItemFromVariant(variant, qty)],
        };

        let tries = 0;
        const maxTries = 10;

        const tick = () => {
            if (window.gtag) {
                window.gtag("event", eventName, payload);
                return;
            }
            tries += 1;
            if (tries >= maxTries) return;
            setTimeout(tick, 200);
        };

        tick();
    }

    function trackGaViewItem(productData) {
        if (typeof window === "undefined") return;
        if (!productData?.id) return;

        const sentKey = `ga_view_item_sent_${productData.id}_${currency?.code || "HUF"}`;
        if (sessionStorage.getItem(sentKey)) return;

        const firstSellableVariant =
            productData?.product_variants?.find(v => !isAddonVariant(v)) ||
            productData?.product_variants?.[0] ||
            null;

        const item = firstSellableVariant
            ? buildGaItemFromVariant(firstSellableVariant, 1)
            : {
                item_id: String(productData.id),
                item_name: productData?.name?.trim() || "product",
                item_category: "ticket",
                quantity: 1,
            };

        const payload = {
            currency: currency?.code || "HUF",
            value: Number(item.price) || 0,
            items: [item],
        };

        let tries = 0;
        const maxTries = 10;

        const tick = () => {
            if (window.gtag) {
                window.gtag("event", "view_item", payload);
                sessionStorage.setItem(sentKey, "1");
                return;
            }
            tries += 1;
            if (tries >= maxTries) return;
            setTimeout(tick, 200);
        };

        tick();
    }

    ////////////////////////////////////////////////////////////////////////////
    // ZOOM TILTÁS
    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        const getScale = () => {
            const s = window?.visualViewport?.scale;
            return typeof s === "number" ? s : 1;
        };

        const onWheel = (e) => {
            const isZoomGesture = e.ctrlKey || e.metaKey; // Win/Linux: Ctrl, Mac: Cmd
            if (!isZoomGesture) return;

            const scale = getScale();

            // Ctrl/Cmd + wheel:
            // deltaY < 0 => zoom in
            // deltaY > 0 => zoom out
            const isZoomIn = e.deltaY < 0;
            const isZoomOut = e.deltaY > 0;

            if (isZoomIn) {
                // ✅ zoom-in tiltás
                e.preventDefault();
                return;
            }

            // ✅ zoom-out engedett, ha már nagyítva van
            if (isZoomOut && scale <= 1) {
                // 100%-on ne "zoomoljon" sehova
                e.preventDefault();
            }
        };

        const onKeyDown = (e) => {
            const mod = e.ctrlKey || e.metaKey;
            if (!mod) return;

            const key = e.key;
            const isPlus = key === "+" || key === "="; // sok kiosztáson a + az "="
            const isMinus = key === "-";
            const isZero = key === "0";

            // ✅ zoom-in tiltás
            if (isPlus) {
                e.preventDefault();
                return;
            }

            // ✅ zoom-out / reset engedett, ha már nagyítva van
            const scale = getScale();
            if ((isMinus || isZero) && scale <= 1) {
                // 100%-on ne változtasson
                e.preventDefault();
            }
        };

        window.addEventListener("wheel", onWheel, { passive: false });
        window.addEventListener("keydown", onKeyDown);

        return () => {
            window.removeEventListener("wheel", onWheel);
            window.removeEventListener("keydown", onKeyDown);
        };
    }, []);

    useEffect(() => {
        const getScale = () => {
            const s = window?.visualViewport?.scale;
            return typeof s === "number" ? s : 1;
        };

        const preventGestureAt100 = (e) => {
            // gesture eseményeknél nincs jó iránydetektálás,
            // ezért: csak 100%-on blokkoljuk, hogy ne tudjon "bezoomolni",
            // de ha már bezoomolt, hagyjuk, hogy vissza tudjon csípni.
            if (getScale() <= 1) {
                e.preventDefault();
            }
        };

        document.addEventListener("gesturestart", preventGestureAt100);
        document.addEventListener("gesturechange", preventGestureAt100);
        document.addEventListener("gestureend", preventGestureAt100);

        return () => {
            document.removeEventListener("gesturestart", preventGestureAt100);
            document.removeEventListener("gesturechange", preventGestureAt100);
            document.removeEventListener("gestureend", preventGestureAt100);
        };
    }, []);

    ////////////////////////////////////////////////////////////////////////////
    // ZOOM TILTÁS VÉGE
    ////////////////////////////////////////////////////////////////////////////

    function getVariantById(variantId) {
        return product?.product_variants?.find(v => String(v.id) === String(variantId)) || null;
    }

    function buildGaItemFromVariant(variant, qty = 1) {
        const unit = Number(
            variant?.price?.unit_price_final ??
            variant?.price?.base_final ??
            variant?.base_price ??
            0
        );

        return {
            item_id: variant?.documentId || variant?.sku || String(variant?.id),
            item_name: (variant?.name?.trim() || "variant"),
            item_variant: variant?.name?.trim() || undefined,
            price: unit,
            quantity: qty,
            // opcionális, de hasznos:
            item_category: "ticket",
        };
    }

    function renderVariantPrice(variant, mode = "default") {
        if (!variant) return null;

        const original =
            Number(variant.original_price) ||
            Number(variant.price?.original_amount) ||
            Number(variant.price?.base_original) ||
            Number(variant.base_price) ||
            0;

        const final =
            Number(variant.price?.base_final) ||
            Number(variant.base_price) ||
            0;

        const hasDiscount = original > final;

        const originalFormatted = formatCurrency(original);
        const finalFormatted = formatCurrency(final);

        if (mode === "default") {
            return (
                <div className="flex flex-col items-end text-sm">

                    {/* ÁTHÚZOTT */}
                    {hasDiscount && (
                        <span className="line-through text-gray-400 dark:text-gray-500 text-xs">
                            {originalFormatted} {currency?.symbol ?? ""}
                        </span>
                    )}

                    {/* VÉGLEGES ÁR */}
                    <span className="font-semibold">
                        {finalFormatted} {currency?.symbol ?? ""}
                    </span>

                    {/* PRICE TYPE FELIRAT */}
                    {variant.price?.applied_price_type && hasDiscount && (
                        <span className="text-xs text-green-600 dark:text-green-400">
                            {variant.price.applied_price_type.name}
                        </span>
                    )}
                </div>
            );
        }

        if (mode === "custom") {
            return (
                <div className="flex flex-col text-sm">

                    <div className="flex items-baseline justify-end gap-2 min-h-[1.75rem]">

                        {/* ÁTHÚZOTT – bal oldalt */}
                        <span
                            className={`text-xs line-through text-gray-400 dark:text-gray-500 ${hasDiscount ? "opacity-100" : "opacity-0"
                                }`}
                        >
                            {originalFormatted} {currency?.symbol ?? ""}
                        </span>

                        {/* VÉGLEGES ÁR – fix pozíció */}
                        <span className="text-xl font-semibold">
                            {finalFormatted} {currency?.symbol ?? ""}
                        </span>

                    </div>

                    {/* PRICE TYPE FELIRAT */}
                    {variant.price?.applied_price_type && hasDiscount && (
                        <span className="text-xs text-green-600 dark:text-green-400 mt-0.5">
                            {variant.price.applied_price_type.name}
                        </span>
                    )}

                </div>
            );
        }

    }

    function renderHandlingFee(variant) {
        const fee = Number(variant?.handling_fee) || 0;
        if (fee <= 0) return null;

        return (
            <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                + {formatCurrency(fee)} {currency?.symbol ?? ""} rendszerhasználati díj
            </span>
        );
    }

    function getWebStocks(variant) {
        const stocks = variant?.product_variant_stocks;
        if (!Array.isArray(stocks)) return [];
        return stocks.filter((s) => s?.warehouse?.use_for_web_stock === true);
    }

    function hasStockConfig(variant) {
        const stocks = variant?.product_variant_stocks;
        return Array.isArray(stocks) && stocks.length > 0;
    }


    function getVariantStockQty(variant) {
        const stocks = variant?.product_variant_stocks;
        if (!Array.isArray(stocks) || stocks.length === 0) return null; // nincs semmi -> mint eddig

        const webStocks = getWebStocks(variant);
        if (webStocks.length === 0) return 0; // van config, de nincs webes -> 0

        return webStocks.reduce((sum, s) => sum + (Number(s?.quantity) || 0), 0);
    }

    function isOutOfStock(variant) {
        // nincs semmi stock sor -> ne tiltsunk (mint eddig)
        if (!hasStockConfig(variant)) return false;

        // van valamilyen config -> web qty alapján (ha nincs webes, getVariantStockQty() 0-t ad)
        const qty = getVariantStockQty(variant);
        return (qty ?? 0) <= 0;
    }

    function isAddonVariant(variant) {
        return variant?.product_variant_attributes?.some(
            (attr) => attr?.attribute?.key === "is_addon" && attr?.custom_value_text === "true"
        );
    }

    function shouldShowStock(variant) {
        return variant?.product_variant_attributes?.some(
            (attr) => attr?.attribute?.key === "show_stock" && attr?.custom_value_text === "true"
        );
    }


    function renderStock(variant) {
        // addonra sose mutatjuk
        if (isAddonVariant(variant)) return null;

        if (!shouldShowStock(variant)) return null;

        // ha nincs stock config -> ne mutassunk semmit (mint eddig)
        if (!hasStockConfig(variant)) return null;

        const qty = getVariantStockQty(variant);
        return (
            <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t("product.stock")}: {qty}
            </span>
        );
    }

    const [priceType, setPriceType] = useState(initialPriceType);

    useEffect(() => {
        if (!product?.id) return;

        // 1️⃣ Socket létrehozása csak egyszer
        if (!socket) {
            console.log("🔌 Connecting to Socket.IO...");

            socket = io(SOCKET_URL, {
                withCredentials: true,
                transports: ["websocket"],
            });

            socket.on("connect", () => {
                console.log("🟢 Socket connected", socket.id);

                setWebsocketFailed(false);

                // Ha már van product.id → join
                if (product?.id) {
                    const room = `product_${product.id}`;
                    console.log("🏠 Joining room:", room);
                    socket.emit("room:join", room);
                }
            });

            socket.on("connect_error", () => {
                console.warn("⚠️ Websocket connection failed → fallback mode");
                setWebsocketFailed(true);
            });

            socket.on("disconnect", () => {
                console.warn("⚠️ Websocket disconnected → fallback mode");
                setWebsocketFailed(true);
            });

            // Seat update listener csak egyszer
            socket.on("seat:update", (payload) => {
                if (payload.productId !== product.id) return;

                setReservedSeats(prev => {
                    const existing = prev.filter(s => s.seatId !== payload.seatId);

                    if (payload.action === "released") {
                        // töröljük a széket a listából
                        return existing;
                    }

                    // hozzáadjuk vagy frissítjük
                    return [...existing, {
                        seatId: payload.seatId,
                        status: payload.status   // "mine" | "other"
                    }];
                });
            });
        }

        // 2️⃣ Ha product.id változik → joinolj új roomba
        if (socket && socket.connected) {
            const room = `product_${product.id}`;
            console.log("🏠 Joining room after product change:", room);
            socket.emit("room:join", room);
        }

        // 3️⃣ Oldallelépéskor takarítsunk
        return () => {
            if (socket) {
                console.log("👋 Disconnecting socket...");
                socket.off("seat:update");
                socket.disconnect();
                socket = null;
            }
        };
    }, [product?.id]);

    const pushToast = (type, title, message) => {
        const id = Date.now();

        setToasts(prev => [...prev, { id, type, title, message }]);

        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, 3000);
    };

    const handleBackendToasts = (json) => {
        if (Array.isArray(json.toasts)) {
            for (const tObj of json.toasts) {
                pushToast(
                    tObj.type,
                    t(`toast.${tObj.type}`),
                    t(tObj.translationKey, tObj.params)
                );
            }
        } else if (json.toast) {
            // régi API-k miatt még támogatjuk
            pushToast(json.toast.type, json.toast.title, json.toast.message);
        }
    };

    const handleSeatSelect = async (seatId, force = false) => {

        if (!force) {
            setPendingSeatId(seatId);
            setShowSeatModal(true);
            return;
        }

        if (seatConfirmLoading) return; // dupla katt / Enter ellen
        setSeatConfirmLoading(true);

        try {

            const seat = seats.find(s => s.id === seatId);
            const seatCategoryOfSeat = seat.category;

            const variantsForSeat = seatmapVariants.filter((variant) => {
                const seatCategoryAttr = variant.product_variant_attributes?.find(
                    (attr) => attr?.attribute?.key === "seat_category"
                );

                const seatCategoryValue =
                    seatCategoryAttr?.attribute_value?.key ||
                    seatCategoryAttr?.custom_value_text ||
                    seatCategoryAttr?.custom_value_number;

                return seatCategoryValue === seatCategoryOfSeat;
            });

            // ❗ Ha NINCS ilyen kategória → stop
            if (!variantsForSeat.length) {
                console.error("No variant found for seat category:", seatCategoryOfSeat);
                return;
            }

            // ❗ Ha csak 1 variáns van → ugyanaz mint régen
            const variantForSeat = variantsForSeat[0];



            // 🔥 Itt a helyes fetch
            const res = await fetch(`/api/cart/bulk-add`, {
                method: "POST",
                body: JSON.stringify({
                    items: [
                        {
                            product: product.id,
                            product_variant: variantForSeat.id,
                            quantity: 1,
                            seat: seatId
                        }
                    ]
                })
            });

            const data = await res.json();

            if (res.ok) {
                const opAction = getCartOpAction(data, { type: "seat", seatId });
                const eventName = opAction === "removed" ? "remove_from_cart" : "add_to_cart";
                trackGaCart(eventName, variantForSeat, 1);
            }

            handleBackendToasts(data);


            // Kijelölés helyben
            setSelectedSeats(prev => [...prev, seatId]);

            // 🔥 ÚJRA LEKÉRDEZI A FOGLALT HELYEKET
            if (websocketFailed) {
                console.log("👉 Fallback: újra lekérdezem a foglalt helyeket");
                await loadReservedSeats(product.id, seatmapKey);
            }

        } finally {
            setSeatConfirmLoading(false);
        }
    };

    const handleSeatSelectWithVariant = async (seatId, variantId) => {
        const res = await fetch(`/api/cart/bulk-add`, {
            method: "POST",
            body: JSON.stringify({
                items: [
                    {
                        product: product.id,
                        product_variant: variantId,
                        quantity: 1,
                        seat: seatId
                    }
                ]
            })
        });

        const data = await res.json();

        if (res.ok) {
            const variant = getVariantById(variantId);
            trackGaCart("add_to_cart", variant, 1);
        }

        handleBackendToasts(data);
    };



    const handleSimpleAddToCart = async (variantId) => {

        const res = await fetch(`/api/cart/bulk-add`, {
            method: "POST",
            body: JSON.stringify({
                items: [{
                    product: product.id,
                    product_variant: variantId,
                    quantity: 1
                }]
            })
        });

        const data = await res.json();

        if (res.ok) {
            const variant = getVariantById(variantId);
            trackGaCart("add_to_cart", variant, 1);
        }

        handleBackendToasts(data);
    };

    const [reservedSeats, setReservedSeats] = useState([]);

    const loadReservedSeats = async (productId, seatmapKey) => {
        if (!productId || !seatmapKey) return;

        const reqId = ++reservedReqIdRef.current;

        // ✅ indul az új foglaltság betöltés -> AZONNAL loader
        setReservedSeatsLoadedOnce(false);
        setReservedSeatsLoading(true);

        try {
            const res = await fetch(
                `/api/seats/reserved-seats?productId=${productId}&seatmapKey=${encodeURIComponent(seatmapKey)}`
            );
            const json = await res.json();

            // ✅ csak a legfrissebb request írhat state-et
            if (reqId !== reservedReqIdRef.current) return;

            setReservedSeats(json.seats || []);
            setReservedSeatsLoadedOnce(true);
        } catch (err) {
            if (reqId !== reservedReqIdRef.current) return;
            console.error("Error loading reserved seats:", err);

            // ❗ ne ragadjon örökké loaderen
            setReservedSeats([]);
            setReservedSeatsLoadedOnce(true);
        } finally {
            if (reqId !== reservedReqIdRef.current) return;
            setReservedSeatsLoading(false);
        }
    };

    ///
    // --- SEATMAP gyűjtés ---
    const seatmapItems = product?.product_variants
        ?.map((variant) =>
            variant.product_variant_attributes?.find(
                (attr) => attr?.attribute?.key === "seatmap"
            )?.attribute_value
        )
        ?.filter(Boolean);

    const uniqueSeatmaps = [];
    const usedSeatmapKeys = new Set();

    seatmapItems?.forEach((item) => {
        if (!usedSeatmapKeys.has(item.key)) {
            usedSeatmapKeys.add(item.key);
            uniqueSeatmaps.push(item);
        }
    });

    const seatmapKey = uniqueSeatmaps?.[0]?.key || null;

    const seatmapDisplay =
        uniqueSeatmaps
            .map((item) => t(`seatmap.${item.key}`, item.key))
            .join(", ") || t("product.no.seatmap");

    ///
    ///
    // --- EVENT START TIME gyűjtés ---
    const eventStartItems = product?.product_variants
        ?.filter((variant) => !isAddonVariant(variant)) // ✅ addonok ne kerüljenek bele
        ?.map((variant) =>
            variant.product_variant_attributes?.find(
                (attr) => attr?.attribute?.key === "event_start_time"
            )?.custom_value_date
        )
        ?.filter(Boolean);


    const uniqueEventDates = [];
    const usedEventDates = new Set();

    eventStartItems?.forEach((dateStr) => {
        const time = new Date(dateStr).getTime();
        if (!usedEventDates.has(time)) {
            usedEventDates.add(time);
            uniqueEventDates.push(dateStr);
        }
    });

    const eventStartDisplay =
        uniqueEventDates
            .map((dateStr) =>
                new Date(dateStr).toLocaleString("hu-HU", {
                    dateStyle: "medium",
                    timeStyle: "short",
                    timeZone: "Europe/Budapest",
                })
            )
            .join(", ") || t("product.no.start.time");

    ////////////////////////////////////////////////////////////////////////////////////////////////////
    // KÉPEK
    ////////////////////////////////////////////////////////////////////////////////////////////////////
    // Hover image state nem kell
    // const [hoverImage, setHoverImage] = useState(null);

    const PLACEHOLDER_IMAGE = "/placeholder.webp";

    // 0) Video (mindig product alatt)
    const mainVideo = product?.videos?.[0]?.url || null;

    // 1) Product image
    let mainImage = product?.images?.[0]?.url || null;

    // 2) Ha nincs → első variáns image
    if (!mainImage) {
        for (const variant of product?.product_variants || []) {
            if (variant?.images?.[0]?.url) {
                mainImage = variant.images[0].url;
                break;
            }
        }
    }

    // 3) Ha továbbra sincs → placeholder
    mainImage = mainImage || PLACEHOLDER_IMAGE;

    const hasMediaGrid2 = Boolean(mainImage && mainVideo);

    ////////////////////////////////////////////////////////////////////////////////////////////////////
    //
    ////////////////////////////////////////////////////////////////////////////////////////////////////

    const loadProduct = async () => {
        if (!slug) return;

        try {
            const res = await fetch(`/api/product/${slug}`);

            const json = await res.json();

            setProduct(json.data?.[0] || null);
            setPriceType(json.price_type || null);
        } catch (e) {
            console.error("Product load error", e);
        }
    };

    useEffect(() => {
        loadProduct();
    }, [slug]);

    useEffect(() => {
        if (!product?.id) return;
        trackGaViewItem(product);
    }, [product?.id, currency?.code]);

    // ------------------------------------------------------------
    // VARIÁNSOK SZÉTVÁLASZTÁSA (seatmap, addon, normal)
    // ------------------------------------------------------------
    const seatmapVariants = product?.product_variants?.filter(variant =>
        !isGiftVariant(variant) &&
        variant.product_variant_attributes?.some(
            attr => attr.attribute?.key === "is_seatmap" && attr.custom_value_text === "true"
        )
    ) || [];

    const addonVariants = product?.product_variants?.filter(variant =>
        !isGiftVariant(variant) &&
        variant.product_variant_attributes?.some(
            attr => attr.attribute?.key === "is_addon" && attr.custom_value_text === "true"
        )
    ) || [];

    const normalVariants = product?.product_variants?.filter(variant =>
        !isGiftVariant(variant) &&
        !variant.product_variant_attributes?.some(
            attr =>
                (attr.attribute?.key === "is_seatmap" && attr.custom_value_text === "true") ||
                (attr.attribute?.key === "is_addon" && attr.custom_value_text === "true")
        )
    ) || [];

    const hasSeatmap = seatmapVariants.length > 0;

    // ------------------------------------------------------------
    // ÁRAK seatmap variánsokból
    // ------------------------------------------------------------
    const seatCategoryPrices = {};
    seatmapVariants?.forEach((variant) => {
        const selectedPrice = getPriceForCurrency(variant);
        if (!selectedPrice) return;

        const seatCategoryAttr = variant.product_variant_attributes?.find(
            (attr) => attr?.attribute?.key === "seat_category"
        );

        const seatCategory =
            seatCategoryAttr?.attribute_value?.key ||
            seatCategoryAttr?.custom_value_text ||
            seatCategoryAttr?.custom_value_number ||
            null;

        if (seatCategory) {
            seatCategoryPrices[seatCategory] = {
                amount: selectedPrice.amount,
                currency: selectedPrice.currency?.symbol,
            };
        }
    });

    // ------------------------------------------------------------
    // SZÍNEK seatmap variánsokból (seat_category -> style)
    // ------------------------------------------------------------
    const seatCategoryStyles = {};
    seatmapVariants?.forEach((variant) => {
        const seatCategoryAttr = variant.product_variant_attributes?.find(
            (attr) => attr?.attribute?.key === "seat_category"
        );

        const seatCategory =
            seatCategoryAttr?.attribute_value?.key ||
            seatCategoryAttr?.custom_value_text ||
            seatCategoryAttr?.custom_value_number ||
            null;

        if (!seatCategory) return;

        // 1) ha van Strapi-ból jövő szín a variánson/attribútumon, azt használd (példa)
        // pl. variant.color_hex vagy seatCategoryAttr.attribute_value?.meta?.color stb.
        // MOST: fallbackeljünk egy fix mappingre
        seatCategoryStyles[seatCategory] = categoryColorStyleFallback(seatCategory);
    });

    function categoryColorStyleFallback(category) {
        switch (category) {
            case "vip":
                return { backgroundImage: "linear-gradient(to right, #14b8a6, #34d399, #0e7490)", color: "#fff" };
            case "premium":
                return { backgroundColor: "#6D28D9", color: "#fff" };
            case "first":
                return { backgroundColor: "#4F46E5", color: "#fff" };
            case "second":
                return { backgroundColor: "#D946EF", color: "#111827" };
            case "third":
                return { backgroundColor: "#22C55E", color: "#222" };
            case "normal":
                return { backgroundColor: "#38BDF8", color: "#222" };
            case "stairs":
                return { backgroundColor: "#D1D5DB", color: "#222" };
            default:
                return { backgroundColor: "#86EFAC", color: "#14532d" };
        }
    }


    useEffect(() => {
        if (!product?.id || !seatmapKey) return;
        loadReservedSeats(product.id, seatmapKey);
    }, [product?.id, seatmapKey]);

    const [seatmapData, setSeatmapData] = useState(initialSeatmapData);

    useEffect(() => {
        // route váltáskor reset + új SSG preload átvétele
        setSeatmapData(initialSeatmapData ?? null);
        setSeats(initialSeats ?? []);
        setSelectedSeats([]);

        // ✅ NE nullázd a reservedSeats-et, csak mondd azt, hogy még nem kész
        setReservedSeatsLoadedOnce(false);
        setReservedSeatsLoading(true);

        setPendingSeatId(null);
        setShowSeatModal(false);
    }, [slug, initialSeatmapData, initialSeats]);

    const loadedSeatmapKeyRef = useRef(null);

    const loadSeats = async (seatmapKey, { force = false } = {}) => {
        if (!seatmapKey) return;

        // csak akkor skip, ha ugyanazt a seatmapKey-t már betöltöttük
        if (!force && loadedSeatmapKeyRef.current === seatmapKey && seats?.length > 0) return;

        setSeatsLoading(true);

        try {
            const res = await fetch(`/api/seats/${seatmapKey}/seats-fast`);
            const json = await res.json();
            const seatmap = json.data?.[0];

            setSeatmapData(seatmap || null);
            setSeats(seatmap?.seats || []);
            loadedSeatmapKeyRef.current = seatmapKey;

        } catch (err) {
            console.error("Seat load error", err);
        } finally {
            setSeatsLoading(false);
        }
    };


    const publicAddress = seatmapData?.public_address || null;

    useEffect(() => {
        if (!seatmapKey) return;
        loadSeats(seatmapKey, { force: true });
    }, [seatmapKey]);


    // ------------------------------------------------------------
    // UI
    // ------------------------------------------------------------

    const SeatVariantsBlock = () => {
        if (seatmapVariants.length === 0) return null;

        return (
            <BoxBlock
                title={t("product.seat.variants")}
                icon={<Info />}
                className="divide-y divide-gray-100 dark:divide-zinc-700"
            >
                {seatmapVariants.map((variant) => {
                    const seatCategoryAttr = variant.product_variant_attributes?.find(
                        (attr) => attr?.attribute?.key === "seat_category"
                    );

                    const seatCategory =
                        seatCategoryAttr?.attribute_value?.key ||
                        seatCategoryAttr?.custom_value_text ||
                        seatCategoryAttr?.custom_value_number ||
                        null;

                    const chipStyle = seatCategory ? seatCategoryStyles?.[seatCategory] : null;

                    return (
                        // SeatVariantsBlock -> seatmapVariants.map((variant) => { ... return ( ... ) })

                        <div key={variant.id} className="px-6 py-3">
                            <div className="flex flex-wrap items-start gap-x-3 gap-y-1">
                                <div className="flex items-start gap-2 flex-1 min-w-0">
                                    {chipStyle && (
                                        <span
                                            aria-hidden
                                            className="inline-block w-3.5 h-3.5 rounded-sm shrink-0 mt-1"
                                            style={{ ...chipStyle, border: "1px solid rgba(0,0,0,0.12)" }}
                                        />
                                    )}

                                    {/* Név + leírás */}
                                    <div className="min-w-0 flex flex-col">
                                        <span className="font-medium text-gray-800 dark:text-gray-200 min-w-0 break-words">
                                            {variant?.name?.trim() || variant?.sku || ""}
                                        </span>

                                        {variant.description && (
                                            <span className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 whitespace-pre-wrap break-words">
                                                {variant.description}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="ml-auto shrink-0 text-right">
                                    {renderVariantPrice(variant)}
                                </div>
                            </div>
                        </div>

                    );
                })}

                {/* ✅ FIX: Foglalt sor */}
                <div className="px-6 py-3">
                    <div className="flex flex-wrap items-start gap-x-3 gap-y-1">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                            <span
                                aria-hidden
                                className="inline-block w-3.5 h-3.5 rounded-sm shrink-0 border border-black/10 bg-gray-300/70 dark:bg-zinc-700/70"
                            />
                            <span className="font-medium text-gray-800 dark:text-gray-200 min-w-0 break-words">
                                Foglalt
                            </span>
                        </div>
                        <div className="ml-auto shrink-0 text-right" />
                    </div>
                </div>
            </BoxBlock>
        );
    };

    const leftColumn = (
        <>
            {/* LOCATION + TIME BOX */}
            {(uniqueSeatmaps.length > 0 || uniqueEventDates.length > 0) && (
                <BoxBlock
                    title={t("product.location.and.time")}
                    icon={<Navigation />}
                    className="divide-y divide-gray-100 dark:divide-zinc-700"
                >
                    {/* SEATMAP */}
                    <div className="flex items-center gap-3 px-3 py-3">
                        <div className="p-1 bg-gray-100 dark:bg-zinc-700 rounded-md">
                            <Navigation className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                        </div>

                        <div className="flex flex-col">
                            <span className="font-medium text-gray-800 dark:text-gray-200">
                                {t("variant.seatmap.location")}
                            </span>
                            <span className="text-gray-600 dark:text-gray-300">
                                {seatmapDisplay}
                            </span>
                        </div>
                    </div>

                    {/* ✅ PUBLIC ADDRESS */}
                    {publicAddress && (
                        <div className="flex items-center gap-3 px-3 py-3">
                            <div className="p-1 bg-gray-100 dark:bg-zinc-700 rounded-md">
                                <MapPin className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                            </div>

                            <div className="flex flex-col">
                                <span className="font-medium text-gray-800 dark:text-gray-200">
                                    {t("variant.seatmap.address") /* ha nincs, mehet simán: "Cím" */}
                                </span>
                                <span className="text-gray-600 dark:text-gray-300">
                                    {publicAddress}
                                </span>
                            </div>
                        </div>
                    )}

                    {/* EVENT START TIME */}
                    {uniqueEventDates.length > 0 && (
                        <div className="flex items-center gap-3 px-3 py-3">
                            <div className="p-1 bg-gray-100 dark:bg-zinc-700 rounded-md">
                                <Calendar className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                            </div>

                            <div className="flex flex-col">
                                <span className="font-medium text-gray-800 dark:text-gray-200">
                                    {t("variant.start.time")}
                                </span>
                                <span className="text-gray-600 dark:text-gray-300">
                                    {eventStartDisplay}
                                </span>
                            </div>
                        </div>
                    )}
                </BoxBlock>
            )}

            {seatmapVariants.length > 0 && (
                <div className="hidden lg:block 2xl:hidden">
                    <SeatVariantsBlock />
                </div>
            )}

            <div
                className={[
                    "grid grid-cols-1 gap-4 items-start",
                    hasMediaGrid2 ? "max-lg:grid-cols-2" : "",
                ].join(" ")}
            >
                {mainImage && (
                    <BoxBlock title={t("product.images")} className="" icon={<ImageIcon />}>
                        <div className="relative w-full aspect-square bg-gray-50 dark:bg-zinc-800">
                            <Image
                                src={mainImage}
                                alt={product?.name ? t(product.name) : ""}
                                fill
                                sizes="(max-width: 1024px) 50vw, 420px"
                                className="object-contain"
                                priority
                                unoptimized
                            />
                        </div>
                    </BoxBlock>
                )}
                {mainVideo && (
                    <div className="block lg:hidden">
                        <BoxBlock title={t("product.videos")} className="" icon={<ImageIcon />}>
                            <div className="relative w-full aspect-[9/16] bg-gray-50 dark:bg-zinc-800">
                                <video
                                    src={mainVideo}
                                    controls
                                    autoPlay
                                    muted
                                    playsInline
                                    loop
                                    preload="metadata"
                                    className="absolute inset-0 w-full h-full object-contain"
                                />
                            </div>
                        </BoxBlock>

                    </div>
                )}
            </div>
        </>
    );

    const centerColumn = (
        <>
            {/* --- START: PRODUCT STATUS WARNING --- */}
            {product?.product_status && product.product_status !== "active" && (
                <AlertBlock type="warn">
                    <div className="flex items-start gap-2">
                        <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
                        <div className="text-sm">
                            <div className="font-semibold">
                                {t(`product.not.active.${product.product_status}.title`)}
                            </div>
                            <div className="mt-1 text-gray-700 dark:text-gray-300">
                                {t(`product.not.active.${product.product_status}`)}
                            </div>
                        </div>
                    </div>
                </AlertBlock>
            )}
            {/* --- END: PRODUCT STATUS WARNING --- */}


            {product.description && (
                <BoxBlock title={t("product.description")} icon={<LetterText />}>
                    {/* KATTINTHATÓ TARTALOM */}
                    <div
                        role="button"
                        tabIndex={0}
                        onClick={() => setShowFullDescription((prev) => !prev)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setShowFullDescription((prev) => !prev);
                            }
                        }}
                        className={`
        relative text-sm leading-relaxed cursor-pointer select-none
        ${showFullDescription ? "max-h-none" : "max-h-40 overflow-hidden"}
      `}
                    >
                        {renderDescriptionHtml(product.description)}

                        {/* FADE effect — csak akkor, ha nincs teljesen kibontva */}
                        {!showFullDescription && (
                            <div className="absolute bottom-0 left-0 w-full h-16
          bg-gradient-to-t from-white dark:from-zinc-900 to-transparent"
                            />
                        )}
                    </div>

                    {/* BUTTON (opcionális, megmarad) */}
                    <div>
                        <button
                            onClick={(e) => {
                                e.stopPropagation(); // ne toggle-öljön kétszer (container + button)
                                setShowFullDescription((prev) => !prev);
                            }}
                            className="mt-2 w-full py-2 rounded-md text-sm font-medium 
          text-blue-600 dark:text-blue-400 
          bg-gray-100 dark:bg-zinc-800 
          hover:bg-gray-200 dark:hover:bg-zinc-700 
          transition cursor-pointer"
                        >
                            {showFullDescription ? t("product.read.less") : t("product.read.more")}
                        </button>
                    </div>
                </BoxBlock>
            )}

            {/* --- START: ALERT BLOCK --- */}
            {product.alert_type && product.alert_type !== "none" && product.alert_text && (
                <>
                    <AlertBlock type={product.alert_type}>
                        {renderDescriptionHtml(product.alert_text)}
                    </AlertBlock>
                </>
            )}
            {/* --- END: ALERT BLOCK --- */}

            {/* ✅ MOBILON itt legyen, a description alatt */}
            {seatmapVariants.length > 0 && (
                <div className="lg:hidden">
                    <SeatVariantsBlock />
                </div>
            )}

            {hasSeatmap && (
                <BoxBlock
                    title={t("product.seating")}
                    icon={<Sofa />}
                    className="h-[80vh] md:h-screen 2xl:h-[60vh]"
                >
                    {(seatsLoading || reservedSeatsLoading || !reservedSeatsLoadedOnce) ? (
                        <div className="w-full h-full flex items-center justify-center py-10">
                            <Loader2 className="w-10 h-10 animate-spin text-black dark:text-white" />
                        </div>
                    ) : !seatsLoading && seats.length === 0 ? (
                        // a te error UI-d maradhat itt változtatás nélkül
                        <div className="w-full h-full flex items-center justify-center py-10 px-4">
                            {/* ... a "Jelenleg nem tudjuk betölteni a székeket" blokkod ... */}
                        </div>
                    ) : (
                        <>
                            {seatmapData?.type === "basic" && (
                                <SeatmapInteractionGuard>
                                    <Basic
                                        key={`seatmap-${product?.id}-${seatmapKey}`}
                                        selectedSeats={selectedSeats}
                                        onSelect={handleSeatSelect}
                                        seatCategoryPrices={seatCategoryPrices}
                                        seatCategoryStyles={seatCategoryStyles}
                                        seatsFromApi={seats}
                                        reservedSeats={reservedSeats}
                                        canvasSize={{
                                            width: seatmapData.canvas_width ?? 10000,
                                            height: seatmapData.canvas_height ?? 6000,
                                        }}
                                        scaleConfig={seatmapData?.scale_config ?? undefined}
                                        stage={seatmapData?.stage ?? null}
                                    />
                                </SeatmapInteractionGuard>
                            )}

                            {seatmapData?.type === "editor" && (
                                <Editor
                                    key={`seatmap-${product?.id}-${seatmapKey}`}
                                    selectedSeats={selectedSeats}
                                    onSelect={handleSeatSelect}
                                    seatCategoryPrices={seatCategoryPrices}
                                    seatsFromApi={seats}
                                    reservedSeats={reservedSeats}
                                    canvasSize={{
                                        width: seatmapData.canvas_width ?? 10000,
                                        height: seatmapData.canvas_height ?? 6000,
                                    }}
                                    scaleConfig={seatmapData?.scale_config ?? undefined}
                                    stage={seatmapData?.stage ?? null}
                                />
                            )}
                        </>
                    )}
                </BoxBlock>
            )}

            {addonVariants.length > 0 && (
                <BoxBlock
                    title={t("product.addons")}
                    icon={<Wine />}
                    className="divide-y divide-gray-100 dark:divide-zinc-700"
                >
                    {addonVariants.map((variant) => {
                        const out = isOutOfStock(variant);
                        const loading = !!simpleAddLoading[variant.id];

                        return (
                            <div
                                key={variant.id}
                                className={`px-5 py-3 ${out ? "opacity-50 cursor-not-allowed" : ""
                                    }`}
                            >
                                <div className="flex flex-col gap-2">
                                    {/* 1) NÉV: felső sor, széltől szélig */}
                                    <div className="w-full">
                                        <span className="font-medium text-gray-900 dark:text-gray-100 block">
                                            {variant?.name?.trim() || variant?.sku || ""}
                                        </span>
                                    </div>

                                    {/* 2) ALATTA: minden a régi logikával */}
                                    <div className="flex items-center justify-between gap-4">
                                        <div className="min-w-0 flex flex-col">
                                            <span className="text-sm text-gray-600 dark:text-gray-300">
                                                {renderVariantPrice(variant, "custom")}
                                                {renderHandlingFee(variant)}
                                            </span>
                                        </div>

                                        <AddToCartButton
                                            disabled={out}
                                            loading={loading}
                                            labelKey={
                                                out
                                                    ? "product.out_of_stock"
                                                    : loading
                                                        ? "product.add.to.cart.loading"
                                                        : "product.add.to.cart"
                                            }
                                            onClick={() => handleSimpleAddToCartWithLoading(variant.id)}
                                        />
                                    </div>
                                </div>


                                {variant.description && (() => {
                                    const isOpen = !!expandedAddonDescriptions[variant.id];
                                    const isLong = variant.description.length > 160;

                                    return (
                                        <div className="mt-2">
                                            {/* KATTINTHATÓ LEÍRÁS */}
                                            <div
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => toggleAddonDesc(variant.id)}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter" || e.key === " ") {
                                                        e.preventDefault();
                                                        toggleAddonDesc(variant.id);
                                                    }
                                                }}
                                                className={`
          relative text-sm leading-relaxed text-gray-500 dark:text-gray-400 text-justify
          cursor-pointer select-none
          ${isOpen ? "max-h-none" : "max-h-10 overflow-hidden"}
        `}
                                            >
                                                <div className="flex gap-3 bg-white dark:bg-zinc-900">
                                                    {variant?.images?.[0]?.url && (
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();              // ne nyissa/csukja a leírást
                                                                setActiveAddonImage(variant.images[0].url);
                                                                setShowAddonImageModal(true);
                                                            }}
                                                            className="shrink-0 w-20 h-20 rounded-md overflow-hidden bg-gray-50 dark:bg-zinc-800 border border-gray-100 dark:border-zinc-700 relative cursor-pointer"
                                                            aria-label="Kép megnyitása"
                                                        >
                                                            <Image
                                                                src={variant.images[0].url}
                                                                alt=""
                                                                fill
                                                                sizes="80px"
                                                                className="object-contain"
                                                                unoptimized
                                                            />
                                                        </button>
                                                    )}


                                                    <div className="min-w-0 text-sm leading-relaxed text-gray-500 dark:text-gray-400 whitespace-pre-wrap">
                                                        {variant.description}
                                                    </div>
                                                </div>


                                                {!isOpen && isLong && (
                                                    <div className="absolute bottom-0 left-0 w-full h-10
            bg-gradient-to-t from-white dark:from-zinc-900 to-transparent"
                                                    />
                                                )}
                                            </div>

                                            {/* GOMB (opcionális, megmarad) */}
                                            {isLong && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation(); // ne toggle-öljön kétszer
                                                        toggleAddonDesc(variant.id);
                                                    }}
                                                    className="mt-2 w-full py-2 rounded-md text-sm font-medium 
            text-blue-600 dark:text-blue-400 
            bg-gray-100 dark:bg-zinc-800 
            hover:bg-gray-200 dark:hover:bg-zinc-700 
            transition cursor-pointer"
                                                >
                                                    {isOpen ? t("product.read.less") : t("product.read.more")}
                                                </button>
                                            )}
                                        </div>
                                    );
                                })()}


                            </div>
                        );
                    })}
                </BoxBlock>
            )}

            {normalVariants.length > 0 && (
                <BoxBlock
                    title={t("product.normal.options")}
                    icon={<Sofa />}
                    className="divide-y divide-gray-100 dark:divide-zinc-700"
                >
                    {normalVariants.map((variant) => {
                        const out = isOutOfStock(variant);
                        const loading = !!simpleAddLoading[variant.id];

                        return (
                            <div
                                key={variant.id}
                                className={`px-5 py-3 ${out ? "opacity-50 cursor-not-allowed" : ""}`}
                            >
                                <div className="flex flex-col gap-2">
                                    {/* 1) NÉV: felső sor */}
                                    <div className="w-full">
                                        <span className="font-medium text-gray-900 dark:text-gray-100 block">
                                            {variant?.name?.trim() || variant?.sku || ""}
                                        </span>
                                    </div>

                                    {/* 2) ALATTA: ár + gomb */}
                                    <div className="flex items-center justify-between gap-4">
                                        <div className="min-w-0 flex flex-col">
                                            <span className="text-sm text-gray-600 dark:text-gray-300">
                                                {renderVariantPrice(variant, "default")}
                                            </span>

                                            {/* STOCK */}
                                            {renderStock(variant)}
                                        </div>

                                        <AddToCartButton
                                            disabled={out}
                                            loading={loading}
                                            labelKey={
                                                out
                                                    ? "product.out_of_stock"
                                                    : loading
                                                        ? "product.add.to.cart.loading"
                                                        : "product.add.to.cart"
                                            }
                                            onClick={() => handleSimpleAddToCartWithLoading(variant.id)}
                                        />
                                    </div>
                                </div>

                                {/* ✅ LEÍRÁS: ugyanaz mint addon */}
                                {variant.description && (() => {
                                    const isOpen = !!expandedNormalDescriptions[variant.id];
                                    const isLong = variant.description.length > 160;

                                    return (
                                        <div className="mt-2">
                                            <div
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => toggleNormalDesc(variant.id)}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter" || e.key === " ") {
                                                        e.preventDefault();
                                                        toggleNormalDesc(variant.id);
                                                    }
                                                }}
                                                className={`
                    relative text-sm leading-relaxed text-gray-500 dark:text-gray-400 text-justify
                    cursor-pointer select-none
                    ${isOpen ? "max-h-none" : "max-h-10 overflow-hidden"}
                  `}
                                            >
                                                <div className="flex gap-3 bg-white dark:bg-zinc-900">
                                                    {variant?.images?.[0]?.url && (
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setActiveAddonImage(variant.images[0].url);
                                                                setShowAddonImageModal(true);
                                                            }}
                                                            className="shrink-0 w-20 h-20 rounded-md overflow-hidden bg-gray-50 dark:bg-zinc-800 border border-gray-100 dark:border-zinc-700 relative cursor-pointer"
                                                            aria-label="Kép megnyitása"
                                                        >
                                                            <Image
                                                                src={variant.images[0].url}
                                                                alt=""
                                                                fill
                                                                sizes="80px"
                                                                className="object-contain"
                                                                unoptimized
                                                            />
                                                        </button>
                                                    )}

                                                    <div className="min-w-0 text-sm leading-relaxed text-gray-500 dark:text-gray-400 whitespace-pre-wrap">
                                                        {variant.description}
                                                    </div>
                                                </div>

                                                {!isOpen && isLong && (
                                                    <div className="absolute bottom-0 left-0 w-full h-10 bg-gradient-to-t from-white dark:from-zinc-900 to-transparent" />
                                                )}
                                            </div>

                                            {isLong && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        toggleNormalDesc(variant.id);
                                                    }}
                                                    className="mt-2 w-full py-2 rounded-md text-sm font-medium 
                      text-blue-600 dark:text-blue-400 
                      bg-gray-100 dark:bg-zinc-800 
                      hover:bg-gray-200 dark:hover:bg-zinc-700 
                      transition cursor-pointer"
                                                >
                                                    {isOpen ? t("product.read.less") : t("product.read.more")}
                                                </button>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>
                        );
                    })}
                </BoxBlock>
            )}

            {product?.product_status === "active" && (
                <div className="">
                    <Link
                        href="/cart"
                        className="
      block w-full text-center
      bg-black text-white dark:bg-white dark:text-black
      py-3 rounded-xl font-semibold
      shadow-md active:scale-95 transition cursor-pointer
      inline-flex items-center justify-center gap-2
    "
                    >
                        {/* opcionális ikon */}
                        {/* <ShoppingCart className="w-5 h-5" /> */}
                        Tovább a kosárhoz
                    </Link>
                </div>
            )}

            {seatmapData?.latitude && seatmapData?.longitude && (
                <Leafleet
                    key={`leaflet-${slug}-${seatmapData.latitude}-${seatmapData.longitude}`}
                    lat={seatmapData.latitude}
                    lng={seatmapData.longitude}
                    label={seatmapData.public_name}
                />
            )}
        </>
    );

    const rightColumn = (
        <>
            {/* ✅ DESKTOPON itt marad */}
            {seatmapVariants.length > 0 && (
                <div className="hidden md:block">
                    <SeatVariantsBlock />
                </div>
            )}

            {normalVariants.length > 0 && (
                <BoxBlock
                    title={t("product.normal.options")}
                    icon={<Info />}
                    className="divide-y divide-gray-100 dark:divide-zinc-700"
                >
                    {normalVariants.map((variant) => {
                        return (
                            <div key={variant.id} className="px-6 py-3">
                                <div className="flex flex-wrap items-start gap-x-3 gap-y-1">
                                    {/* NÉV: bal oldalt, törhet */}
                                    <span className="font-medium text-gray-800 dark:text-gray-200 flex-1 min-w-0 break-words">
                                        {variant?.name?.trim() || variant?.sku || ""}
                                    </span>

                                    {/* ÁR: jobbra, ha nem fér ki → leugrik új sorba, de jobbra marad */}
                                    <div className="ml-auto shrink-0 text-right">
                                        {renderVariantPrice(variant)}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </BoxBlock>
            )}

            {/* VARIATIONS / PRICES BOX */}
            {seatmapVariants.length > 0 && (
                <BoxBlock
                    title={t("product.seating.info")}
                    icon={<Info />}
                    className="divide-y divide-gray-100 dark:divide-zinc-700"
                >
                    <div
                        className="px-5 py-3 text-sm text-gray-600 dark:text-gray-300"
                    >
                        {t("product.seating.info.long.text")}
                    </div>
                </BoxBlock>
            )}

            {/* ADD-ONS BOX */}
            {addonVariants?.length > 0 && (
                <BoxBlock
                    title={t("product.addons")}
                    icon={<Wine />}
                    className="divide-y divide-gray-100 dark:divide-zinc-700"
                >
                    {addonVariants.map((variant, index) => {
                        return (
                            <div key={index} className="px-5 py-3">
                                <div className="flex flex-wrap items-start gap-x-3 gap-y-1">
                                    {/* NÉV: bal oldalt, törhet */}
                                    <span className="font-medium text-gray-800 dark:text-gray-200 flex-1 min-w-0 break-words">
                                        {variant?.name?.trim() || variant?.sku || ""}
                                    </span>

                                    {/* ÁR: jobbra, ha nem fér ki → leugrik új sorba, de jobbra marad */}
                                    <div className="ml-auto shrink-0 text-right">
                                        {renderVariantPrice(variant)}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </BoxBlock>
            )}


            {mainVideo && (
                <BoxBlock title={t("product.videos")} className="" icon={<ImageIcon />}>
                    <div className="relative w-full aspect-[9/16] bg-gray-50 dark:bg-zinc-800">
                        <video
                            src={mainVideo}
                            controls
                            autoPlay
                            muted
                            playsInline
                            loop
                            preload="metadata"
                            className="absolute inset-0 w-full h-full object-contain"
                        />
                    </div>
                </BoxBlock>
            )}
        </>
    );

    // --- SEO DESCRIPTION EXTRACTOR ---
    function stripHtmlForMeta(input) {
        const s = (input ?? "").toString();

        const noTags = s.replace(/<[^>]*>/g, " ");

        const decoded = noTags
            .replace(/&nbsp;|&#160;/gi, " ")
            .replace(/&amp;/gi, "&")
            .replace(/&quot;/gi, '"')
            .replace(/&#39;|&apos;/gi, "'")
            .replace(/&lt;/gi, "<")
            .replace(/&gt;/gi, ">");

        return decoded.replace(/\s+/g, " ").trim();
    }

    function ellipsis(str, max) {
        const s = (str ?? "").toString().trim();
        if (!s) return "";
        if (s.length <= max) return s;
        return s.slice(0, Math.max(0, max - 3)).trimEnd() + "...";
    }

    const fullDescription = stripHtmlForMeta(product?.description);
    const shortDescription = ellipsis(fullDescription, 140);

    // IMAGE:
    const seoImage = mainImage || "/default-og.png";

    const SITE_URL =
        process.env.NEXT_PUBLIC_SITE_URL;

    const absUrl = slug ? `${SITE_URL.replace(/\/$/, "")}/${slug}` : "";

    const absImage = mainImage
        ? (mainImage.startsWith("http")
            ? mainImage
            : `${SITE_URL.replace(/\/$/, "")}${mainImage.startsWith("/") ? "" : "/"}${mainImage}`)
        : "";

    const eventDates = getEventStartDates(product, isAddonVariant);
    const isEvent = eventDates.length > 0;

    const jsonLd = buildJsonLd({
        product,
        isAddonVariant,
        absUrl,
        absImage,
        currencyCode: currency?.code || "HUF",
        publicAddress,
        seatmapName: seatmapDisplay,
    });


    return (
        <>
            <Head>
                {jsonLd && (
                    <script
                        type="application/ld+json"
                        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
                    />
                )}
            </Head>

            <Layout
                title={product.name}
                description={shortDescription}
                image={seoImage}
                ogPath={slug ? `/${slug}` : ""}  // <-- csak slugból
                ogType={isEvent ? "event" : "product"}
                left={leftColumn}
                center={centerColumn}
                right={rightColumn}
            >
                <ToastContainer
                    toasts={toasts}
                    removeToast={(id) => setToasts(prev => prev.filter(t => t.id !== id))}
                />

                <SeatModal
                    open={showSeatModal}
                    pendingSeatId={pendingSeatId}
                    seats={seats}
                    reservedSeats={reservedSeats}
                    seatmapVariants={seatmapVariants}
                    seatCategoryPrices={seatCategoryPrices}
                    t={t}
                    renderVariantPrice={renderVariantPrice}
                    renderHandlingFee={renderHandlingFee}
                    seatConfirmLoading={seatConfirmLoading}
                    onClose={() => {
                        setShowSeatModal(false);
                        setPendingSeatId(null);
                    }}
                    onConfirmSeat={(seatId) => handleSeatSelect(seatId, true)}
                    onSelectVariant={(seatId, variantId) => handleSeatSelectWithVariant(seatId, variantId)}
                />

                {showAddonImageModal && activeAddonImage && (
                    <div
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
                        onClick={() => {
                            setShowAddonImageModal(false);
                            setActiveAddonImage(null);
                        }}
                    >
                        <div className="relative w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
                            <div className="relative w-full aspect-square max-h-[80vh] bg-white dark:bg-zinc-900 rounded-xl overflow-hidden">
                                {/* X a képen */}
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowAddonImageModal(false);
                                        setActiveAddonImage(null);
                                    }}
                                    className="absolute top-3 right-3 z-10 p-2 rounded-full bg-black/60 hover:bg-black/80 text-white backdrop-blur
                 transition cursor-pointer"
                                    aria-label="Bezárás"
                                >
                                    <X className="w-5 h-5" />
                                </button>

                                <Image
                                    src={activeAddonImage}
                                    alt=""
                                    fill
                                    sizes="(max-width: 768px) 90vw, 900px"
                                    className="object-contain"
                                    priority
                                    unoptimized
                                />
                            </div>
                        </div>

                    </div>
                )}

            </Layout>
        </>
    );
}

export async function getStaticPaths() {
    const res = await fetch(`${process.env.STRAPI_URL}/api/products`);
    const json = await res.json();

    const paths = json.data.map(item => ({
        params: { slug: item.slug }
    }));

    return {
        paths,
        fallback: "blocking",
    };
}

async function baseGetStaticProps({ params }) {
    const { slug } = params;

    const res = await fetch(`${process.env.STRAPI_URL}/api/product/${slug}`);
    if (res.status === 404) return { notFound: true };

    const json = await res.json();
    if (!json.data || json.data.length === 0) return { notFound: true };

    const product = json.data[0];

    async function fetchProductsList() {
        const url =
            process.env.STRAPI_URL +
            "/api/products" +
            "?sort[0]=sort_order:asc" +
            "&filters[product_status][$notIn][0]=draft" +
            "&filters[product_status][$notIn][1]=unlisted" +
            "&pagination[pageSize]=100" +
            "&populate[images][fields][0]=url" +
            "&populate[product_variants][fields][0]=name" +
            "&populate[product_variants][fields][1]=sku" +
            "&populate[product_variants][populate][images][fields][0]=url" +
            "&populate[product_variants][populate][images][fields][1]=alternativeText" +
            "&populate[product_variants][populate][product_variant_attributes][fields][0]=custom_value_text" +
            "&populate[product_variants][populate][product_variant_attributes][fields][1]=custom_value_number" +
            "&populate[product_variants][populate][product_variant_attributes][fields][2]=custom_value_date" +
            "&populate[product_variants][populate][product_variant_attributes][populate][attribute][fields][0]=key" +
            "&populate[product_variants][populate][product_variant_attributes][populate][attribute][fields][1]=label" +
            "&populate[product_variants][populate][product_variant_attributes][populate][attribute][fields][2]=input_type" +
            "&populate[product_variants][populate][product_variant_attributes][populate][attribute_value][fields][0]=label" +
            "&populate[product_variants][populate][product_variant_attributes][populate][attribute_value][fields][1]=key";

        const res = await fetch(url);
        const json = await res.json();
        return json.data || [];
    }

    // seatmapKey kinyerés (ugyanaz a logika, csak server oldalon)
    const seatmapItems =
        product?.product_variants
            ?.map((variant) =>
                variant.product_variant_attributes?.find(
                    (attr) => attr?.attribute?.key === "seatmap"
                )?.attribute_value
            )
            ?.filter(Boolean) || [];

    const used = new Set();
    const uniqueSeatmaps = [];
    for (const item of seatmapItems) {
        if (!used.has(item.key)) {
            used.add(item.key);
            uniqueSeatmaps.push(item);
        }
    }

    const seatmapKey = uniqueSeatmaps?.[0]?.key || null;

    // ✅ seatmap + seats preload
    let initialSeatmapData = null;
    let initialSeats = [];

    if (seatmapKey) {
        try {
            const seatsRes = await fetch(
                `${process.env.STRAPI_URL}/api/seats/${encodeURIComponent(seatmapKey)}/seats-fast`
            );
            const seatsJson = await seatsRes.json();
            const seatmap = seatsJson.data?.[0] || null;

            initialSeatmapData = seatmap;
            initialSeats = seatmap?.seats || [];
        } catch (e) {
            console.error("SSG seat preload error", e);
        }
    }

    const products = await fetchProductsList();

    return {
        props: {
            initialProduct: product,
            initialPriceType: json.price_type || null,
            initialSeatmapData,
            initialSeats,
            products,
        },
        revalidate: 3600,
    };
}

ProductPage.enableMoreProducts = true;

export const getStaticProps = withI18nStaticProps(baseGetStaticProps, { locale: "hu" });